import { useEffect, useState } from 'react';
import ExecuteAxios from 'Components/ExecuteAxios/ExecuteAxios';
import MiddlePanel from 'Components/MiddlePanel/MiddlePanel';
import Panel from 'Components/Panel/Panel';
import ReactDataPickerComp from 'Components/ReactDataPicker/ReactDataPickerComp';
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator';
import PanelListDefault from 'Components/PanelListDefault/PanelListDefault';
import InputField from 'Components/Input/InputField';
import MandatoryIconField from 'Components/MandatoryIconField';
import ComboBox from 'Components/ComboBox/ComboBox';
import OptionsDataByAttributePath from 'Components/Options/optionsData/OptionsDataByAttributePath';
import "./DuplicateDayStyle.css";

const DuplicateDay = ({
    productionId,
    showPanelDuplicate,
    onUpdateShowPanelDuplicate,
    onUpdateLoadingProductDaysPanel,
    onUpdateProductionDays,
    onUpdateReloadProductionList,
    onUpdateResetProductionDay,
    duplicateDaySelected,
    setDuplicateDaySelected,
    sourceDayId,
    setSourceDayId,
    setShowEmployeeAllocated
}) => {
    const [productionDaysList, setProductionDaysList] = useState(null)
    const [loadingProductionDays, setLoadingProductionDays] = useState(false)

    const [sourceEmployeesIds, setSourceEmployeesIds] = useState(null)
    const [employeesList, setEmployeesList] = useState(null)
    const [employeesIdsSelecteds, setEmployeesIdsSelecteds] = useState([])
    const [loadingEmployees, setLoadingEmployees] = useState(false)
    const [duplicating, setDuplicating] = useState(false)

    const [sectorIdSelected, setSectorIdSelected] = useState(null)
    const [sectors, setSectors] = useState(null)
    const [groupIdSelected, setGroupIdSelected] = useState(null)
    const [groups, setGroups] = useState(null)

    const sectorOptions = OptionsDataByAttributePath(sectors || [], ['description'], null, true)
    const groupOptions = OptionsDataByAttributePath(groups || [], ['description'], null, true)

    const productionDaysAttributeList = [
        { name: "id" },
        { name: "totalMinutesPerDay", isVisible: false },
        { name: "workedMinutes", isVisible: false },
        { name: "minValueWorked", isVisible: false },
        { name: "completedPieces", isVisible: false },
        { name: "username", isVisible: false },
        { name: "dtUpdate", isVisible: false }
    ];

    const employeesAttributeList = [
        { name: "id", allowsMultipleRecordsSelected: true }
    ];

    useEffect(() => {
        // O painel fica montado mesmo fechado: só busca ao abrir
        if (!showPanelDuplicate) {
            return;
        }
        setSourceDayId(null)
        setDuplicateDaySelected(null)
        setSourceEmployeesIds(null)
        setEmployeesList(null)
        setEmployeesIdsSelecteds([])
        setSectorIdSelected(null)
        setGroupIdSelected(null)
        getProductionDaysToDuplicate(productionId)

        if (!sectors) {
            ExecuteAxios.api('get', '/sector').then(setSectors)
        }
        if (!groups) {
            ExecuteAxios.api('get', '/employee-group/lookup/').then(setGroups)
        }
    }, [showPanelDuplicate])

    useEffect(() => {
        setSourceEmployeesIds(null)
        if (sourceDayId) {
            getSourceEmployeesIds(sourceDayId)
        }
    }, [sourceDayId])

    // Ao trocar o dia de origem ou a data, volta a marcar os colaboradores do dia de origem
    useEffect(() => {
        if (!showPanelDuplicate || !sourceEmployeesIds || !duplicateDaySelected) {
            setEmployeesList(null)
            setEmployeesIdsSelecteds([])
            return;
        }
        getEmployeesToDuplicate(sourceEmployeesIds)
    }, [sourceEmployeesIds, duplicateDaySelected])

    // Filtros só limitam a lista: mantém a seleção do que continuar visível
    useEffect(() => {
        if (!showPanelDuplicate || !sourceEmployeesIds || !duplicateDaySelected) {
            return;
        }
        getEmployeesToDuplicate(employeesIdsSelecteds)
    }, [sectorIdSelected, groupIdSelected])

    async function getProductionDaysToDuplicate(productionId) {
        try {
            setLoadingProductionDays(true)
            const parameters = { productionId: productionId }
            setProductionDaysList(await ExecuteAxios.api('get', `/production-day/get-production-days-to-duplicate/`, { params: parameters }))
        } finally {
            setLoadingProductionDays(false)
        }
    }

    async function getSourceEmployeesIds(productionDayId) {
        const parameters = { productionDayId: productionDayId }
        const employeesIds = await ExecuteAxios.api('get', `/employee-day/get-employee-ids-by-production-day/`, { params: parameters })
        setSourceEmployeesIds(employeesIds || [])
    }

    async function getEmployeesToDuplicate(idsToKeepSelected) {
        try {
            setLoadingEmployees(true)
            const parameters = { productionDay: duplicateDaySelected, sectorId: sectorIdSelected, employeeGroupId: groupIdSelected }
            const employees = await ExecuteAxios.api('get', `/employee-day/get-employees-list-to-duplicate/`, { params: parameters })
            setEmployeesList(employees)

            const availableIds = (employees || []).map(employee => employee.id)
            setEmployeesIdsSelecteds(idsToKeepSelected.filter(id => availableIds.includes(id)))
        } finally {
            setLoadingEmployees(false)
        }
    }

    async function duplicateProductionDay() {
        try {
            setDuplicating(true)
            onUpdateLoadingProductDaysPanel(true)
            const response = await ExecuteAxios.api('post', `/production-day/duplicate/`, {
                id: sourceDayId,
                productionId: productionId,
                productionDay: duplicateDaySelected,
                employeesId: employeesIdsSelecteds
            });

            if (response && response != 1) {
                onUpdateShowPanelDuplicate(false)
                onUpdateProductionDays(true)
                onUpdateReloadProductionList(true)
                onUpdateResetProductionDay(true)
            }

            if (response == 1) {
                setShowEmployeeAllocated(true)
            }

            return response;
        } finally {
            setDuplicating(false)
            onUpdateLoadingProductDaysPanel(false)
        }
    }

    function closeDuplicateDayPanel() {
        onUpdateShowPanelDuplicate(false)
    }

    function getEmployeesListOrMessage() {
        if (!sourceDayId) {
            return <span className='duplicate-day-message'>Selecione o dia que deseja duplicar.</span>
        }
        if (!duplicateDaySelected) {
            return <span className='duplicate-day-message'>Informe o dia para duplicar.</span>
        }
        return (
            <LoadingIndicator loaded={!loadingEmployees}>
                <div className='duplicate-day-employees' style={{ height: '100%' }}>
                    <PanelListDefault
                        onUpdateIdSelected={setEmployeesIdsSelecteds}
                        idsSelected={employeesIdsSelecteds}
                        data={employeesList}
                        attributeList={employeesAttributeList}
                    />
                </div>
            </LoadingIndicator>
        )
    }

    const sourceEmployeesAvailable = sourceEmployeesIds && employeesList
        ? employeesList.filter(employee => sourceEmployeesIds.includes(employee.id)).length
        : null;

    // Com outra quantidade o backend não copia as marcações: o balanceamento vem vazio
    const differentEmployeesQuantity = !!sourceEmployeesIds
        && employeesIdsSelecteds.length > 0
        && employeesIdsSelecteds.length !== sourceEmployeesIds.length;

    return (
        <MiddlePanel isVisible={showPanelDuplicate}>
            <Panel style={{
                width: '960px',
                maxWidth: 'calc(100vw - 32px)',
                height: '700px',
                maxHeight: 'calc(100vh - 32px)',
                borderRadius: '10px',
                backgroundColor: '#f5f5f5;',
                border: '1px solid #ccc',
                boxShadow: '0px 0px 10px rgba(0, 0, 0, 30%)'
            }}>
                <LoadingIndicator loaded={!duplicating}>
                <div className='duplicate-day-body'>
                    <div className='duplicate-day-filters'>
                        <span className='duplicate-day-filters-title'>Filtros</span>

                        <div className='duplicate-day-attribute'>
                            <div style={{ display: 'flex', flexDirection: 'row' }}>
                                <label>Duplicar para o dia</label>
                                <MandatoryIconField />
                            </div>
                            <ReactDataPickerComp
                                selected={duplicateDaySelected}
                                onChange={(date) => { setDuplicateDaySelected(date) }}
                                wrapperClassName={'width-full'}
                                className='validate-attribute width-full cursor-pointer'
                                setter={setDuplicateDaySelected}
                            />
                        </div>

                        <div className='duplicate-day-attribute'>
                            <label>Setor</label>
                            <ComboBox setAttribute={setSectorIdSelected}
                                optionsData={sectorOptions}
                                value={sectorIdSelected}
                                defaultValue={sectorIdSelected}
                            />
                        </div>

                        <div className='duplicate-day-attribute'>
                            <label>Grupo</label>
                            <ComboBox setAttribute={setGroupIdSelected}
                                optionsData={groupOptions}
                                value={groupIdSelected}
                                defaultValue={groupIdSelected}
                            />
                        </div>

                        <div className='duplicate-day-attribute'>
                            <label>Colaboradores no dia de origem:</label>
                            <InputField
                                value={sourceEmployeesIds ? sourceEmployeesIds.length : ''}
                                readOnly={true}
                                disabled={true}
                            />
                        </div>

                        <div className='duplicate-day-attribute'>
                            <label>Disponíveis na nova data:</label>
                            <InputField
                                value={sourceEmployeesAvailable ?? ''}
                                readOnly={true}
                                disabled={true}
                            />
                        </div>

                        <div className='duplicate-day-attribute'>
                            <label>Quantidade colaboradores:</label>
                            <InputField
                                value={employeesIdsSelecteds.length}
                                readOnly={true}
                                disabled={true}
                            />
                        </div>

                        {differentEmployeesQuantity &&
                            <span className='duplicate-day-warning'>
                                A quantidade de colaboradores é diferente da do dia de origem. O dia será duplicado
                                sem as marcações do balanceamento, que precisará ser feito no dia novo.
                            </span>
                        }
                    </div>

                    <div className='duplicate-day-lists'>
                        <Panel titlePage={'Duplicar dia'}
                            style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}
                            canViewCRUD={true}
                            postFunction={duplicateProductionDay}
                            saveNameButton='Duplicar'
                            canSaveAfterNewRecord={!!sourceDayId && !!duplicateDaySelected && employeesIdsSelecteds.length > 0}
                            cancelFunction={closeDuplicateDayPanel}
                            cancelNameButton='Fechar'
                            canNewRecord={true}
                            recordsSelected={[]}
                            inserting={true}>
                            <div className='duplicate-day-sections'>
                                <span className='duplicate-day-section-title'>Últimos dias gerados do produto</span>
                                <div className='duplicate-day-section duplicate-day-section-days'>
                                    <LoadingIndicator loaded={!loadingProductionDays}>
                                        <PanelListDefault
                                            onUpdateIdSelected={(ids) => { setSourceDayId(ids && ids.length > 0 ? ids[0] : null) }}
                                            idsSelected={sourceDayId ? [sourceDayId] : []}
                                            data={productionDaysList}
                                            attributeList={productionDaysAttributeList}
                                        />
                                    </LoadingIndicator>
                                </div>

                                <span className='duplicate-day-section-title'>Colaboradores</span>
                                <div className='duplicate-day-section duplicate-day-section-employees'>
                                    {getEmployeesListOrMessage()}
                                </div>
                            </div>
                        </Panel>
                    </div>
                </div>
                </LoadingIndicator>
            </Panel>
        </MiddlePanel >
    );
}

export default DuplicateDay;
